import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Meeting,
  MeetingStatus,
  MeetingType,
  PHIFlag,
  ProcessingResult,
  ProcessingStatus,
} from '@/models/team-meeting.model';
import { OperationsRole } from '@/models/user.model';
import {
  auditCreate,
  auditUpdate,
  auditDelete,
  auditView,
} from '@/utils/audit-integration';
import { AuditEntityType, AuditLogAction } from '@/models/audit-log.model';
import { AuditLogService } from './audit-log.service';

const MEETINGS_COLLECTION = 'teamMeetings';

export class TeamMeetingService {
  /**
   * Create a new meeting
   */
  static async createMeeting(
    meetingData: Partial<Meeting>,
    userId: string,
    userName: string
  ): Promise<string> {
    const now = Timestamp.now();

    const meeting: Partial<Meeting> = {
      ...meetingData,
      status: MeetingStatus.scheduled,
      decisionsCount: 0,
      tasksCount: 0,
      risksCount: 0,
      openQuestionsCount: 0,
      containsPHI: false,
      phiFlags: [],
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, MEETINGS_COLLECTION), meeting);

    // Audit log
    await auditCreate(
      AuditEntityType.teamMeeting,
      docRef.id,
      meetingData.title || 'Untitled Meeting'
    );

    return docRef.id;
  }

  /**
   * Get meeting by ID
   */
  static async getMeetingById(
    id: string,
    logAccess: boolean = false
  ): Promise<Meeting | null> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const meeting = this.docToMeeting(docSnap.id, docSnap.data());

    // Log access if requested
    if (logAccess) {
      auditView(AuditEntityType.teamMeeting, id, meeting.title).catch(console.error);
    }

    return meeting;
  }

  /**
   * Get all meetings
   */
  static async getAllMeetings(): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      orderBy('meetingDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToMeeting(doc.id, doc.data()));
  }

  /**
   * Get recent meetings (limited)
   */
  static async getRecentMeetings(limitCount: number = 10): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      orderBy('meetingDate', 'desc'),
      firestoreLimit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToMeeting(doc.id, doc.data()));
  }

  /**
   * Get meetings by type
   */
  static async getMeetingsByType(type: MeetingType): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where('type', '==', type),
      orderBy('meetingDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToMeeting(doc.id, doc.data()));
  }

  /**
   * Get meetings by status
   */
  static async getMeetingsByStatus(status: MeetingStatus): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where('status', '==', status),
      orderBy('meetingDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToMeeting(doc.id, doc.data()));
  }

  /**
   * Get meetings by date range
   */
  static async getMeetingsByDateRange(
    startDate: Date,
    endDate: Date
  ): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where('meetingDate', '>=', Timestamp.fromDate(startDate)),
      where('meetingDate', '<=', Timestamp.fromDate(endDate)),
      orderBy('meetingDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToMeeting(doc.id, doc.data()));
  }

  /**
   * Search meetings by title or description
   */
  static async searchMeetings(searchTerm: string): Promise<Meeting[]> {
    const allMeetings = await this.getAllMeetings();
    const lowerSearch = searchTerm.toLowerCase();

    return allMeetings.filter(meeting =>
      meeting.title.toLowerCase().includes(lowerSearch) ||
      meeting.description?.toLowerCase().includes(lowerSearch) ||
      meeting.attendees.some(attendee => attendee.toLowerCase().includes(lowerSearch))
    );
  }

  /**
   * Update meeting
   */
  static async updateMeeting(
    id: string,
    updates: Partial<Meeting>,
    userId?: string
  ): Promise<void> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(docRef, updateData);

    // Audit log
    await auditUpdate(
      AuditEntityType.teamMeeting,
      id,
      updates.title || 'Meeting'
    );
  }

  /**
   * Delete meeting
   */
  static async deleteMeeting(id: string): Promise<void> {
    const meeting = await this.getMeetingById(id);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    const docRef = doc(db, MEETINGS_COLLECTION, id);
    await deleteDoc(docRef);

    // Audit log
    await auditDelete(
      AuditEntityType.teamMeeting,
      id,
      meeting.title
    );
  }

  /**
   * Process meeting transcript using Cloud Function
   */
  static async processMeetingTranscript(
    meetingId: string,
    transcriptText: string
  ): Promise<ProcessingResult> {
    const functions = getFunctions();
    const processTranscript = httpsCallable<
      { meetingId: string; transcriptText: string },
      ProcessingResult
    >(functions, 'processTranscript');

    try {
      // Update status to processing
      await this.updateMeeting(meetingId, {
        status: MeetingStatus.processing,
        aiProcessingStartedAt: new Date(),
      });

      const result = await processTranscript({ meetingId, transcriptText });
      return result.data;
    } catch (error) {
      // Update status to failed
      await this.updateMeeting(meetingId, {
        status: MeetingStatus.failed,
        aiProcessingError: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Get processing status for a meeting
   */
  static async getProcessingStatus(meetingId: string): Promise<ProcessingStatus> {
    const meeting = await this.getMeetingById(meetingId);

    if (!meeting) {
      throw new Error('Meeting not found');
    }

    const status: ProcessingStatus = {
      status: meeting.status === MeetingStatus.processing ? 'processing' :
              meeting.status === MeetingStatus.processed ? 'completed' :
              meeting.status === MeetingStatus.failed ? 'failed' : 'pending',
      progress: meeting.status === MeetingStatus.processed ? 100 :
                meeting.status === MeetingStatus.processing ? 50 : 0,
      message: this.getStatusMessage(meeting.status),
      startedAt: meeting.aiProcessingStartedAt,
      completedAt: meeting.aiProcessedAt,
      error: meeting.aiProcessingError,
    };

    return status;
  }

  /**
   * Subscribe to meeting changes (real-time updates)
   */
  static subscribeToMeeting(
    meetingId: string,
    callback: (meeting: Meeting | null) => void
  ): () => void {
    const docRef = doc(db, MEETINGS_COLLECTION, meetingId);

    return onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(this.docToMeeting(docSnap.id, docSnap.data()));
      } else {
        callback(null);
      }
    });
  }

  /**
   * Check if user has access to meeting
   */
  static async checkUserAccess(
    meetingId: string,
    userId: string,
    userRole: OperationsRole
  ): Promise<boolean> {
    const meeting = await this.getMeetingById(meetingId);

    if (!meeting) {
      return false;
    }

    // Admins and executives always have access
    if (userRole === OperationsRole.operationsAdmin || userRole === OperationsRole.executive) {
      return true;
    }

    // Public meetings - all operations staff can access
    if (meeting.isPublic) {
      return true;
    }

    // Check if user is in access list
    if (meeting.accessUserIds.includes(userId)) {
      return true;
    }

    // Check if user's role is in access roles
    if (meeting.accessRoles.includes(userRole)) {
      return true;
    }

    return false;
  }

  /**
   * Update meeting access permissions
   */
  static async updateMeetingAccess(
    meetingId: string,
    accessUserIds: string[],
    accessRoles: OperationsRole[],
    isPublic: boolean,
    userId: string
  ): Promise<void> {
    const meeting = await this.getMeetingById(meetingId);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    await this.updateMeeting(meetingId, {
      accessUserIds,
      accessRoles,
      isPublic,
    }, userId);

    // Audit log with details
    await AuditLogService.writeLog({
      action: AuditLogAction.meetingAccessChanged,
      severity: 'warning',
      description: `Meeting access permissions changed for: ${meeting.title}`,
      userId,
      entityType: AuditEntityType.teamMeeting,
      entityId: meetingId,
      entityName: meeting.title,
      success: true,
      details: {
        accessUserIds,
        accessRoles,
        isPublic,
      },
    });
  }

  /**
   * Flag PHI in meeting
   */
  static async flagPHI(
    meetingId: string,
    flags: PHIFlag[]
  ): Promise<void> {
    const meeting = await this.getMeetingById(meetingId);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    await this.updateMeeting(meetingId, {
      containsPHI: true,
      phiFlags: [...meeting.phiFlags, ...flags],
    });

    // Audit log for PHI detection
    await AuditLogService.writeLog({
      action: AuditLogAction.phiDetected,
      severity: 'warning',
      description: `PHI detected in meeting: ${meeting.title}`,
      entityType: AuditEntityType.teamMeeting,
      entityId: meetingId,
      entityName: meeting.title,
      containsPHI: true,
      success: true,
      details: {
        flagsCount: flags.length,
        flags,
      },
    });
  }

  /**
   * Clear a PHI flag
   */
  static async clearPHIFlag(
    meetingId: string,
    flagIndex: number,
    userId: string
  ): Promise<void> {
    const meeting = await this.getMeetingById(meetingId);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    const updatedFlags = meeting.phiFlags.filter((_, index) => index !== flagIndex);

    await this.updateMeeting(meetingId, {
      phiFlags: updatedFlags,
      containsPHI: updatedFlags.length > 0,
    });

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.phiFlagCleared,
      severity: 'info',
      description: `PHI flag cleared in meeting: ${meeting.title}`,
      userId,
      entityType: AuditEntityType.teamMeeting,
      entityId: meetingId,
      entityName: meeting.title,
      success: true,
    });
  }

  /**
   * Get meetings with pending approvals
   */
  static async getMeetingsWithPendingApprovals(): Promise<Meeting[]> {
    const allMeetings = await this.getAllMeetings();

    // Filter meetings that have been processed and might have pending items
    return allMeetings.filter(meeting =>
      meeting.status === MeetingStatus.processed &&
      (meeting.decisionsCount > 0 || meeting.tasksCount > 0 ||
       meeting.risksCount > 0 || meeting.openQuestionsCount > 0)
    );
  }

  /**
   * Convert Firestore document to Meeting model
   */
  private static docToMeeting(id: string, data: any): Meeting {
    return {
      id,
      title: data.title || '',
      description: data.description,
      type: data.type || MeetingType.other,
      status: data.status || MeetingStatus.scheduled,
      transcriptText: data.transcriptText || '',
      transcriptUrl: data.transcriptUrl,
      transcriptFileName: data.transcriptFileName,
      transcriptFileSize: data.transcriptFileSize,
      aiProcessedAt: data.aiProcessedAt?.toDate(),
      aiProcessingDuration: data.aiProcessingDuration,
      aiModelUsed: data.aiModelUsed,
      aiProcessingError: data.aiProcessingError,
      aiProcessingStartedAt: data.aiProcessingStartedAt?.toDate(),
      meetingDate: data.meetingDate?.toDate() || new Date(),
      attendees: data.attendees || [],
      location: data.location,
      duration: data.duration,
      decisionsCount: data.decisionsCount || 0,
      tasksCount: data.tasksCount || 0,
      risksCount: data.risksCount || 0,
      openQuestionsCount: data.openQuestionsCount || 0,
      containsPHI: data.containsPHI || false,
      phiFlags: data.phiFlags || [],
      accessUserIds: data.accessUserIds || [],
      accessRoles: data.accessRoles || [],
      isPublic: data.isPublic || false,
      tags: data.tags || [],
      projectId: data.projectId,
      departmentId: data.departmentId,
      customFields: data.customFields,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  /**
   * Get status message for UI display
   */
  private static getStatusMessage(status: MeetingStatus): string {
    switch (status) {
      case MeetingStatus.scheduled:
        return 'Meeting scheduled';
      case MeetingStatus.processing:
        return 'Processing transcript...';
      case MeetingStatus.processed:
        return 'Processing complete';
      case MeetingStatus.failed:
        return 'Processing failed';
      case MeetingStatus.archived:
        return 'Meeting archived';
      default:
        return 'Unknown status';
    }
  }
}
